/**
 * Search.jsx — Search results page
 * Reads ?q= from URL, fetches public products API with q, displays grid.
 * Handles: missing/empty query, loading, error, no results.
 */

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../../components/common/Layout/Layout';
import ProductCard from '../../components/product/ProductCard/ProductCard';
import { useToast } from '../../context/ToastContext';
import { BRAND } from '../../config/brand';
import './Search.css';

const API_URL  = process.env.REACT_APP_API_URL  || 'http://localhost:5000/api';
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const LIMIT    = 24;
const SKELETONS = 8;

function normaliseProducts(raw) {
  return raw.map((p, i) => ({ ...p, pid: p._id ?? p.id ?? `list-${i}` }));
}

function SkeletonGrid() {
  return (
    <div className="search-grid search-grid--skeleton" aria-busy="true" aria-label="Loading results">
      {Array.from({ length: SKELETONS }).map((_, i) => (
        <div key={`skel-${i}`} className="search-skeleton" aria-hidden="true">
          <div className="search-skeleton__img shimmer" />
          <div className="search-skeleton__body">
            <div className="search-skeleton__line shimmer search-skeleton__line--name" />
            <div className="search-skeleton__line shimmer search-skeleton__line--sub" />
            <div className="search-skeleton__line shimmer search-skeleton__line--price" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="search-error" role="alert">
      <svg className="search-error__icon" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8"  x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <p className="search-error__msg">{message}</p>
      <button type="button" onClick={onRetry} className="search-btn search-btn--primary">Try Again</button>
    </div>
  );
}

function EmptyQueryState() {
  return (
    <div className="search-empty-query" role="status">
      <p className="search-empty-query__title">Search for products</p>
      <p className="search-empty-query__sub">Use the search bar above or try a category below.</p>
      <div className="search-empty-query__ctas">
        <Link to="/shop/men"   className="search-btn search-btn--primary">Shop Men</Link>
        <Link to="/shop/women" className="search-btn search-btn--ghost">Shop Women</Link>
      </div>
    </div>
  );
}

function NoResultsState({ query, trendingProducts = [], loadingTrending }) {
  return (
    <div className="search-no-results" role="status">
      <p className="search-no-results__title">No results for &ldquo;{query}&rdquo;</p>
      <p className="search-no-results__sub">Try different keywords or browse below.</p>
      <div className="search-no-results__ctas">
        <Link to="/shop" className="search-btn search-btn--primary">View all</Link>
        <Link to="/new-arrivals" className="search-btn search-btn--ghost">New arrivals</Link>
      </div>
      {trendingProducts.length > 0 && (
        <div className="search-no-results__trending">
          <p className="search-no-results__trending-title">Popular right now</p>
          <div className="search-grid search-no-results__grid">
            {trendingProducts.map((p, idx) => (
              <div key={p._id || p.pid || idx} role="listitem">
                <ProductCard product={{ ...p, pid: p._id ?? p.pid }} onAddToCart={() => {}} priority={idx < 2} />
              </div>
            ))}
          </div>
        </div>
      )}
      {loadingTrending && (
        <p className="search-no-results__loading">Loading suggestions…</p>
      )}
    </div>
  );
}

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'price-low', label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' },
  { value: 'popular', label: 'Popular' },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get('q') || '').trim();
  const sort = searchParams.get('sort') || 'newest';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10));

  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [loadingTrending, setLoadingTrending] = useState(false);

  const { showToast } = useToast();
  const showToastRef = useRef(showToast);
  useEffect(() => { showToastRef.current = showToast; }, [showToast]);

  useEffect(() => {
    if (!query) {
      setProducts([]);
      setError(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const TIMEOUT_MS = 30_000; // Allow for backend cold start
    let timeoutId;

    setLoading(true);
    setError(null);

    timeoutId = setTimeout(() => controller.abort('timeout'), TIMEOUT_MS);

    const params = new URLSearchParams({
      q: query,
      limit: String(LIMIT),
      page: String(page),
      sort: sort,
      status: 'active',
    });

    fetch(`${API_URL}/products?${params}`, { signal: controller.signal, cache: 'no-store' })
      .then((res) => {
        clearTimeout(timeoutId);
        if (!res.ok) throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });
        return res.json();
      })
      .then((data) => {
        if (data.success && Array.isArray(data.products)) {
          setProducts(normaliseProducts(data.products));
          const pag = data.pagination || {};
          setTotalProducts(pag.totalProducts ?? data.totalProducts ?? data.products.length);
          setTotalPages(pag.totalPages ?? data.totalPages ?? 1);
        } else {
          setProducts([]);
          setTotalProducts(0);
          setTotalPages(0);
        }
        setError(null);
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        if (controller.signal.aborted) return;
        const msg = err.message === 'timeout' || err.name === 'AbortError'
          ? 'Request timed out. Please try again.'
          : 'Search failed. Please try again.';
        setError(msg);
        setProducts([]);
        showToastRef.current(msg, 'error');
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [query, sort, page, fetchKey]);

  // Fetch trending when no results (safe fallback, never 404)
  useEffect(() => {
    if (!query.trim() || loading || error || products.length > 0) {
      setTrendingProducts([]);
      return;
    }
    setLoadingTrending(true);
    const params = new URLSearchParams({ limit: '6', sort: 'popular', status: 'active' });
    fetch(`${API_URL}/products?${params}`, { cache: 'no-store' })
      .then((res) => res.ok ? res.json() : { success: false })
      .then((data) => {
        if (data.success && Array.isArray(data.products))
          setTrendingProducts(normaliseProducts(data.products));
        else setTrendingProducts([]);
      })
      .catch(() => setTrendingProducts([]))
      .finally(() => setLoadingTrending(false));
  }, [query, loading, error, products.length]);

  const handleRetry = useCallback(() => setFetchKey((k) => k + 1), []);
  const handleAddToCart = useCallback((productName) => {
    showToastRef.current(`${productName} added to your bag — check it out when you're ready.`, 'success');
  }, []);

  const pageTitle = query
    ? `Search results for '${query}' | ${BRAND.name}`
    : `Search | ${BRAND.name}`;
  const pageDesc = query
    ? `Search results for "${query}" at ${BRAND.name}.`
    : `Search products at ${BRAND.name}.`;

  const setSort = (v) => {
    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      next.set('sort', v);
      next.delete('page');
      return next;
    });
  };
  const setPage = (p) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(p));
      return next;
    });
  };

  const hasQuery = query.length > 0;
  const showResults = hasQuery && !loading && !error && products.length > 0;
  const showNoResults = hasQuery && !loading && !error && products.length === 0;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={`${SITE_URL}/search${query ? `?q=${encodeURIComponent(query)}` : ''}`} />
        <meta name="robots" content="noindex, follow" />
        {query && (
          <>
            <meta property="og:type" content="website" />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={pageDesc} />
            <meta property="og:url" content={`${SITE_URL}/search?q=${encodeURIComponent(query)}`} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={pageDesc} />
          </>
        )}
      </Helmet>

      <Layout showFooter showBottomTab>
        <main className="search-page" id="main-content">

          <nav className="search-breadcrumb" aria-label="Breadcrumb">
            <ol className="search-breadcrumb__list">
              <li><Link to="/" className="search-breadcrumb__link">Home</Link></li>
              <li className="search-breadcrumb__sep" aria-hidden="true" />
              <li>
                <span aria-current="page" className="search-breadcrumb__current">
                  Search{query ? `: "${query}"` : ''}
                </span>
              </li>
            </ol>
          </nav>

          <header className="search-header">
            <h1 className="search-header__title">{query ? `Results for "${query}"` : 'Search'}</h1>
            {query && (
              <p className="search-header__sub" aria-live="polite">
                {loading ? 'Searching…' : error ? 'Something went wrong.' : products.length > 0
                  ? `${products.length} ${products.length === 1 ? 'result' : 'results'}`
                  : 'No results found.'}
              </p>
            )}
          </header>

          {!hasQuery && <EmptyQueryState />}
          {hasQuery && error && !loading && <ErrorState message={error} onRetry={handleRetry} />}
          {hasQuery && loading && <SkeletonGrid />}
          {showNoResults && (
            <NoResultsState
              query={query}
              trendingProducts={trendingProducts}
              loadingTrending={loadingTrending}
            />
          )}
          {showResults && (
            <div className="search-results">
              <div className="search-results__toolbar">
                <p className="search-results__count" aria-live="polite">
                  {totalProducts} {totalProducts === 1 ? 'result' : 'results'}
                </p>
                <label className="search-results__sort-label">
                  <span className="search-results__sort-text">Sort</span>
                  <select
                    className="search-results__sort-select"
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    aria-label="Sort results"
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="search-grid" role="list" aria-label={`${products.length} search results`}>
                {products.map((product, idx) => {
                  const key = product.pid || product._id || product.id || `product-${idx}`;
                  return (
                    <div key={key} role="listitem">
                      <ProductCard
                        product={product}
                        onAddToCart={handleAddToCart}
                        priority={idx < 4}
                      />
                    </div>
                  );
                })}
              </div>
              {totalPages > 1 && (
                <nav className="search-pagination" aria-label="Search results pagination">
                  <button
                    type="button"
                    className="search-btn search-btn--ghost search-pagination__btn"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </button>
                  <span className="search-pagination__info">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="search-btn search-btn--ghost search-pagination__btn"
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </nav>
              )}
            </div>
          )}
        </main>
      </Layout>
    </>
  );
}
